import { type FC, Fragment, type ReactNode, Suspense } from "react";

import {
  Box,
  Column,
  Drawer as HightouchUIDrawer,
  DrawerBody,
  DrawerHeader,
  type DrawerProps,
  Row,
  Spinner,
} from "@hightouch/htui";

import type { AppDrawerSize } from "./types";
import { Mask, SkeletonItem, SkeletonList } from "src/components/skeletons";
import { useHeaderHeight } from "src/contexts/header-height-context";

const DefaultSuspenseFallback = (
  <Column h="full" w="full" align="center" justify="center">
    <Spinner size="lg" />
  </Column>
);

export const Drawer: FC<
  Readonly<
    Omit<DrawerProps, "size"> & {
      /**
       * `sidebarInset` is an app-only size that fills the main content rectangle
       * (right of the nav rail, below the app header) at full height. It assumes
       * the default `placement="right"` so the gap sits over the sidebar.
       */
      size?: AppDrawerSize;
      isLoading?: boolean;
      suspenseFallback?: ReactNode;
    }
  >
> = ({
  isOpen = false,
  size = "sm",
  isLoading,
  suspenseFallback = DefaultSuspenseFallback,
  children,
  ...props
}) => {
  const { headerHeight } = useHeaderHeight();

  const topOffset = headerHeight + 8;
  const isSidebarInset = size === "sidebarInset";

  return (
    <HightouchUIDrawer
      isOpen={isOpen}
      size={isSidebarInset ? "full" : size}
      {...props}
      contentWrapperStyles={{
        mt: `${topOffset}px`,
        ...(isSidebarInset
          ? {
              // Width = viewport minus the sidebar; the empty gap falls on the
              // left (over the nav rail) thanks to placement="right". The var is
              // published by NavWidthProvider and falls back to 0px when there
              // is no sidebar (e.g. pre-auth).
              maxW: "calc(100% - var(--ht-nav-width, 0px))",
              h: `calc(100% - ${topOffset}px)`,
            }
          : {}),
        ...props.contentWrapperStyles,
      }}
    >
      {isLoading ? (
        <DrawerLoader />
      ) : (
        <Suspense fallback={suspenseFallback}>{children}</Suspense>
      )}
    </HightouchUIDrawer>
  );
};

export const DrawerLoader = () => {
  return (
    <Fragment>
      <DrawerHeader minHeight="141px">
        <Column gap={4} width="100%">
          <Row justifyContent="space-between" width="100%">
            <SkeletonItem borderRadius="lg" h={8} w="320px" />
            <SkeletonItem borderRadius="lg" h={8} w="96px" />
          </Row>
          <SkeletonItem borderRadius="md" h={6} w="265px" />
        </Column>
      </DrawerHeader>
      <DrawerBody bg="base.lightBackground" height="100vh" p={6}>
        <Mask gap={6}>
          {[1, 2, 3].map((i) => (
            <SkeletonList
              key={i}
              borderRadius="md"
              borderWidth="hairline"
              borderStyle="solid"
              borderColor="base.border"
              boxShadow="sm"
              gap={2}
              p={6}
            >
              <Box borderRadius="md" h={6} w="144px" />
              <Box borderRadius="md" h={4} w="353px" />
              <Box borderRadius="md" h={4} w="183px" />
            </SkeletonList>
          ))}
        </Mask>
      </DrawerBody>
    </Fragment>
  );
};
